// Iter-158.13.2.19a — Export download handoff strategy.
//
// Pure, side-effect-free chooser deciding HOW an Export ZIP is
// handed off to the device. Kept as its own module so the strategy
// is unit-testable and the ExportDialog stays UI-only.
//
// Strategies (in preference order):
//
//   1. "share" — use `navigator.share({files})`. On iOS Safari 15+
//      and Android Chrome 80+, this raises the native share sheet
//      so the user can send the ZIP to Files, AirDrop, Mail,
//      WhatsApp, etc. — a genuinely clear "where's my file"
//      handoff. Guarded by:
//        a. `navigator.share` exists.
//        b. `navigator.canShare({ files })` returns `true`.
//        c. File size ≤ SHARE_MAX_BYTES — the API becomes flaky on
//           large multi-MB payloads on older iOS builds.
//
//   2. "download" — the legacy blob `<a download>` path. Works
//      everywhere modern (desktop and mobile) and lands the ZIP in
//      the browser's default download location. No filesystem
//      path is exposed to JS on any platform — the accompanying
//      user-facing copy must never claim OSF picked the location.
//
// The function accepts a `capabilities` object rather than reading
// `window.navigator` directly so tests can inject arbitrary
// browser shapes without polluting jsdom globals.
export const SHARE_MAX_BYTES = 50 * 1024 * 1024; // 50 MB — matches the
// point at which iOS Safari 15/16 has been observed to start
// dropping Web Share sheet invocations for File payloads.

export function pickExportDownloadStrategy({
    sizeBytes,
    capabilities,
} = {}) {
    const nav = capabilities || {};
    const size = typeof sizeBytes === "number" && sizeBytes > 0
        ? sizeBytes
        : null;

    const canShareFiles =
        typeof nav.share === "function" &&
        typeof nav.canShare === "function" &&
        // Some engines only expose canShare({title}) — we need
        // canShare({files: [...]}) to succeed for a File payload.
        // The caller passes the actual File in `sample_file`.
        Boolean(nav.canShareFiles);

    if (
        canShareFiles &&
        (size === null || size <= SHARE_MAX_BYTES)
    ) {
        return "share";
    }
    return "download";
}

// Companion helper: given a fetched Blob and a target filename,
// wrap in a File so `navigator.share({files:[File]})` accepts it.
// Extracted so it's testable and the dialog doesn't inline File
// construction (which older jsdom builds lack).
export function blobToNamedFile(blob, filename) {
    if (typeof File !== "function") {
        // Fallback for very old runtimes / jsdom: the Blob itself
        // is not shareable but is still downloadable.
        return blob;
    }
    return new File([blob], filename, { type: blob.type || "application/zip" });
}

// Iter-158.13.2.19b — outcome→auto-close decision.
//
// The Ready-panel Save/Share button auto-dismisses the Export
// dialog ONLY on a confirmed successful handoff. Cancel of the
// native share sheet and network/filesystem failures keep the
// dialog open so the user can retry from the same Ready state
// — which is the persistent-completion-signal requirement from
// the .19a follow-up UAT ("if the success toast is missed, the
// persistent UI does not make it obvious that the handoff has
// completed").
//
// Kept as a pure helper so the decision is unit-testable without
// React Testing Library — the component simply forwards the
// handoff outcome string.
export const HANDOFF_OUTCOMES = Object.freeze({
    SHARED: "shared",
    DOWNLOADED: "downloaded",
    CANCELLED: "cancelled",
    FAILED: "failed",
});

export function shouldCloseAfterHandoff(outcome) {
    return (
        outcome === HANDOFF_OUTCOMES.SHARED ||
        outcome === HANDOFF_OUTCOMES.DOWNLOADED
    );
}
